"use client"

import {ReactNode, useEffect, useMemo, useRef, useState} from "react";
import {useQuery} from "@tanstack/react-query";
import {AuthContext, AuthUser} from "@/src/auth/useAuth";
import {useIsEmbedded} from "@/src/auth/useIsEmbedded";
import {useDiscord} from "@/src/components/DiscordActivityProvider";

export function AuthProvider({children}: { children: ReactNode }) {
    const isEmbedded = useIsEmbedded();
    const discord = useDiscord(false);
    const embeddedAuthStarted = useRef(false);
    const [embeddedUser, setEmbeddedUser] = useState<AuthUser | null>(null);
    const [embeddedAccessToken, setEmbeddedAccessToken] = useState<string | null>(null);

    const meQuery = useQuery({
        queryKey: ["auth-me"],
        enabled: !isEmbedded,
        retry: false,
        queryFn: async () => {
            const res = await fetch("/api/auth/me");
            if (!res.ok) throw new Error("Not authenticated");
            return res.json() as Promise<AuthUser>;
        }
    });

    useEffect(() => {
        if (!isEmbedded) return;
        if (!discord?.sdk) return;
        if (embeddedAuthStarted.current) return;

        embeddedAuthStarted.current = true;
        (async () => {
            try {
                const authResult = await discord.sdk.commands.authorize({
                    client_id: process.env.NEXT_PUBLIC_DISCORD_CLIENT_ID ?? "",
                    response_type: "code",
                    prompt: "none",
                    scope: ["identify"],
                });

                const tokenResponse = await fetch("/api/auth/discord/token", {
                    method: "POST",
                    headers: {"Content-Type": "application/json"},
                    body: JSON.stringify({code: authResult.code}),
                });
                if (!tokenResponse.ok) throw new Error("Failed to exchange token");
                const {access_token} = await tokenResponse.json() as {access_token: string};
                setEmbeddedAccessToken(access_token);

                await discord.sdk.commands.authenticate({access_token});

                const meResponse = await fetch("/api/auth/discord/me", {
                    method: "POST",
                    headers: {"Content-Type": "application/json"},
                    body: JSON.stringify({access_token}),
                });
                if (!meResponse.ok) throw new Error("Failed to fetch embedded user");
                const user = await meResponse.json() as AuthUser;
                setEmbeddedUser(user);
            } catch (err) {
                console.error("Embedded auth failed", err);
                embeddedAuthStarted.current = false;
            }
        })();
    }, [isEmbedded, discord?.sdk, meQuery.status, meQuery.refetch]);

    const value = useMemo(() => {
        if (embeddedUser) {
            return {
                status: "authenticated" as const,
                user: embeddedUser,
                accessToken: embeddedAccessToken,
                refresh: () => setEmbeddedUser(embeddedUser)
            };
        }
        if (isEmbedded) {
            return {
                status: "unauthenticated" as const,
                user: null,
                accessToken: embeddedAccessToken,
                refresh: () => setEmbeddedUser(null)
            };
        }
        if (meQuery.status === "success") {
            return {status: "authenticated" as const, user: meQuery.data, accessToken: null, refresh: () => meQuery.refetch()};
        }
        if (meQuery.status === "error") {
            return {status: "unauthenticated" as const, user: null, accessToken: null, refresh: () => meQuery.refetch()};
        }
        return {status: "loading" as const, user: null, accessToken: embeddedAccessToken, refresh: () => meQuery.refetch()};
    }, [embeddedUser, isEmbedded, meQuery, embeddedAccessToken]);

    return <AuthContext value={value}>{children}</AuthContext>;
}
